import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

import tr from '../i18n/tr.json';

const resources = {
  en: {
    translation: {
      history: {
        PAYMENT_RECEIVED_FROM_CUSTOMER: 'Payment of {{amount}} {{currency}} received from {{customerName}}',
        ORDER_DEBT_CREATED: 'Debt of {{amount}} {{currency}} created for order #{{orderNumber}}',
        CUSTOMER_REFUND_ISSUED: 'Refund of {{amount}} {{currency}} issued to {{customerName}} - {{reason}}',
        CUSTOMER_REFUND_CREDIT_APPLIED: 'Refund credit of {{amount}} {{currency}} applied to {{customerName}}',
        CUSTOMER_CASHOUT_PROCESSED: 'Cashout of {{amount}} {{currency}} processed for {{customerName}}',
        CUSTOMER_BALANCE_MANUAL_ADJUSTMENT: 'Manual adjustment of {{amount}} {{currency}} for {{customerName}} by {{performedBy}}',
        CUSTOMER_BALANCE_ADJUSTMENT: 'Balance adjustment of {{amount}} {{currency}} for {{customerName}}',
        WALLET_TRANSFER_SENT: 'Transferred {{amount}} {{currency}} to {{toWalletName}}',
        WALLET_TRANSFER_RECEIVED: 'Received {{amount}} {{currency}} from {{fromWalletName}}',
        WALLET_CASA_TRANSFER_SENT: 'Transferred {{amount}} {{currency}} to casa account',
        WALLET_CASA_TRANSFER_RECEIVED: 'Received {{amount}} {{currency}} from {{fromWalletName}}',
        WALLET_DEPOSIT_COMPLETED: 'Deposited {{amount}} {{currency}} to {{walletName}}',
        WALLET_WITHDRAW_COMPLETED: 'Withdrew {{amount}} {{currency}} from {{walletName}}',
        WALLET_CASH_DEPOSIT: 'Cash deposit of {{amount}} {{currency}} to {{walletName}}',
        WALLET_CASH_TRANSFER: 'Cash transfer of {{amount}} {{currency}} to casa',
      },
      nav: {
        dashboard: 'Dashboard',
        orders: 'Orders',
        products: 'Products',
        categories: 'Categories',
        brands: 'Brands',
        collections: 'Collections',
        customers: 'Customers',
        incoming: 'Incoming',
        'incoming-orders': 'Incoming Orders',
        suppliers: 'Suppliers',
        'product-configuration': 'Product Configuration',
        inventory: 'Inventory',
        warehouses: 'Warehouses',
        wallets: 'Wallets',
        treasury: 'Company Treasury',
        'casa-wallet': 'Casa Wallet',
        users: 'Users',
        logs: 'Logs',
        logout: 'Logout',
        brand: 'Brand',
        category: 'Category',
        collection: 'Collection',
        customer: 'Customer',
        user: 'User',
        warehouse: 'Warehouse'
      },
      orders: {
        manageTrack: 'Manage and track all orders',
        newOrder: 'New Order',
        searchPlaceholder: 'Search orders...',
        selectAll: 'Select all on page',
        view: 'View',
        process: 'Process',
        empty: 'No orders found',
        addExpense: 'Add Expense',
        addSales: 'Add Sales',
        addExpenseDescription: 'Record a new expense transaction',
        addSalesDescription: 'Record a new sales transaction',
        toolbar: {
          search: 'Search orders...',
          dateRange: 'Select date range',
          status: 'Filter by status',
          type: 'Filter by type',
          warehouse: 'Filter by warehouse',
          allWarehouses: 'All Warehouses',
          columns: 'Column visibility',
          density: 'Table density',
          compact: 'Compact',
          comfortable: 'Comfortable'
        },
        selection: {
          count: '{{count}} orders selected',
          allMatching: 'All {{count}} matching orders selected',
          selectAllMatching: 'Select all {{total}} matching orders',
          clear: 'Clear selection'
        },
        bulk: {
          actions: 'Bulk Actions',
          updateStatus: 'Update Status',
          assignWarehouse: 'Assign Warehouse',
          print: 'Print Orders',
          cancel: 'Cancel Orders',
          success: 'Bulk action completed successfully',
          error: 'Failed to perform bulk action'
        },
        actions: {
          view: 'View Details',
          process: 'Process Order',
          print: 'Print Order',
          cancel: 'Cancel Order'
        },
        detail: {
          orderActivity: 'Order Activity',
          noActivity: 'No activity recorded',
          orderCreated: 'Order Created',
          orderUpdated: 'Order Updated',
          orderCompleted: 'Order Completed',
          orderCancelled: 'Order Cancelled',
          orderRefunded: 'Order Refunded',
          orderProcessingFinished: 'Order processing finished successfully',
          orderDetailsModified: 'Order details were modified',
          salesRepresentative: 'Sales Representative',
          payment: 'Payment',
          seller: 'Seller',
          orderNotes: 'Order Notes',
        refundReason: 'Refund Reason',
        cancellationReason: 'Cancellation Reason'
      },
      activityTitles: {
        ORDER_CREATED: 'Order Created',
        ORDER_STATUS_CHANGED: 'Status Changed',
        ORDER_CANCELLED: 'Order Cancelled',
        ORDER_UPDATED: 'Order Updated',
        ORDER_ITEM_ADDED: 'Item Added',
        ORDER_ITEM_REMOVED: 'Item Removed',
        ORDER_DEBT_CREATED: 'Debt Recorded',
        ORDER_DEBT_INCREASED: 'Debt Increased',
        ORDER_DEBT_DECREASED: 'Debt Decreased'
      },
      activityDescriptions: {
        ORDER_CREATED: 'Order #{{orderNumber}} created for {{customerName}} ({{itemCount}} items).',
        ORDER_STATUS_CHANGED: 'Status changed from {{oldStatusLabel}} to {{newStatusLabel}}.',
        ORDER_CANCELLED: 'Order cancelled.',
        ORDER_UPDATED: 'Order totals updated.',
        ORDER_ITEM_ADDED: 'Added {{quantity}} × {{productName}} to the order.',
        ORDER_ITEM_REMOVED: 'Removed {{quantity}} × {{productName}} from the order.',
        ORDER_DEBT_CREATED: 'Debt of {{amountLabel}} recorded for this order.',
        ORDER_DEBT_INCREASED: 'Debt increased by {{amountDeltaLabel}}.',
        ORDER_DEBT_DECREASED: 'Debt decreased by {{amountDeltaLabel}}.',
        ORDER_CREDIT_APPLIED: 'Customer credit of {{amountLabel}} automatically applied to order.',
        PAYMENT_RECEIVED: 'Payment of {{amountLabel}} received from customer.',
        ORDER_PAYMENT_ADDED: 'Payment of {{amountLabel}} added to order.'
      },
      activityLabels: {
        reason: 'Reason',
        reference: 'Reference',
        amountIncreased: 'Amount increased by {{amount}}',
        amountDecreased: 'Amount decreased by {{amount}}',
        creditApplied: 'Credit Applied',
        creditAppliedDetail: '💳 Credit Applied: {{amount}}',
        remainingAmount: 'Remaining',
        remainingAmountDetail: '📋 Remaining: {{amount}}',
        fullyPaid: 'Fully Paid',
        fullyPaidDetail: '✅ Fully Paid',
        outstandingDebt: 'Outstanding Debt',
        unset: 'Not set'
      },
      pagination: {
        showing: 'Showing {{start}} to {{end}} of {{total}} orders',
        page: 'Page {{current}} of {{total}}'
      },
        status: {
          all: 'All Status',
          draft: 'Draft',
          pending: 'Pending',
          completed: 'Completed',
          cancelled: 'Cancelled',
          refunded: 'Refunded',
          partially_refunded: 'Partially Refunded',
          PENDING: 'Pending',
          COMPLETED: 'Completed',
          CANCELLED: 'Cancelled',
          REFUNDED: 'Refunded',
          PARTIALLY_REFUNDED: 'Partially Refunded',
          DRAFT: 'Draft'
        },
        type: 'Type',
        types: {
          all: 'All Types',
          SALE: 'Sale',
          RETURN: 'Return',
          EXCHANGE: 'Exchange',
        },
        columns: {
          order: 'Order',
          date: 'Date',
          customer: 'Customer',
          status: 'Status',
          type: 'Type',
          items: 'Items',
          warehouse: 'Warehouse',
          total: 'Total',
          actions: 'Actions'
        },
        customer: 'Customer',
        warehouse: 'Warehouse',
        selectCustomer: 'Select customer',
        selectWarehouse: 'Select warehouse',
        selectProduct: 'Select product',
        noCustomersFound: 'No customers found',
        noWarehousesFound: 'No warehouses found',
        noProductsFound: 'No products found',
        discountPercentage: 'Discount Percentage',
        items: 'Order Items',
        item: 'Item',
        addItem: 'Add Item',
        product: 'Product',
        quantity: 'Quantity',
        unitPrice: 'Unit Price',
        itemNotes: 'Item Notes',
        enterItemNotes: 'Enter item notes',
        notes: 'Order Notes',
        enterNotes: 'Enter order notes',
        notesPlaceholder: 'Order notes...',
        discountPercent: 'Discount %',
        summary: 'Order Summary',
        subtotal: 'Subtotal',
        discount: 'Discount',
        total: 'Total',
        saveChanges: 'Save Changes',
        createOrder: 'Create Order',
        orderCreated: 'Order created successfully',
        createError: 'Failed to create order'
      },
      auth: {
        login: 'Login',
        email: 'Email',
        password: 'Password',
        loginButton: 'Sign In',
        loginError: 'Invalid email or password',
        loading: 'Signing in...',
        welcome: 'Welcome to Warehouse Ukraine',
        loginSubtitle: 'Enter your credentials to access the system',
        toggleLanguage: 'Українська'
      },
      common: {
        search: 'Search',
        add: 'Add',
        edit: 'Edit',
        delete: 'Delete',
        save: 'Save',
        cancel: 'Cancel',
        loading: 'Loading...',
        error: 'Error',
        success: 'Success',
        loadingCategories: 'Loading categories...',
        loadingBrands: 'Loading brands...',
        loadingCollections: 'Loading collections...',
        selectStatus: 'Select status',
        selectReadiness: 'Select readiness',
        selectCategories: 'Select categories...',
        selectBrands: 'Select brands...',
        selectCollections: 'Select collections...',
        selectProduct: 'Select product...',
        selectWarehouse: 'Select warehouse...',
        selectCustomer: 'Select customer...',
        selectSupplier: 'Select supplier...',
        selectExpenseType: 'Select expense type',
        selectWallet: 'Select wallet',
        selectCategory: 'Select category',
        selectBrand: 'Select brand',
        noCategoriesFound: 'No categories found',
        noBrandsFound: 'No brands found',
        noCollectionsFound: 'No collections found',
        noProductsFound: 'No products found',
        noWarehousesFound: 'No warehouses found',
        noCustomersFound: 'No customers found',
        noSuppliersFound: 'No suppliers found',
        noProductsAvailable: 'No products available',
        noItemsFound: 'No items found',
        noActionsAvailable: 'No actions available',
        enterProductName: 'Enter product name',
        enterSku: 'Enter SKU',
        enterDescription: 'Enter description',
        enterExpenseDescription: 'Enter expense description',
        enterReceiptNumber: 'Enter receipt/reference number',
        enterTrackingNumber: 'Enter tracking number...',
        enterShippingAddress: 'Enter shipping address...',
        optional: 'Optional',
        comingSoon: 'Coming Soon',
        management: 'Management',
        filters: 'Filters',
        export: 'Export',
        items: 'Items',
        staff: 'Staff',
        used: 'Used',
        fromLastMonth: 'from last month',
        thisMonth: 'this month',
        fromYesterday: 'from yesterday',
        hoursAgo: 'hours ago',
        dayAgo: 'day ago',
        filter: 'Filter',
        actions: 'Actions',
        name: 'Name',
        description: 'Description',
        status: 'Status',
        createdAt: 'Created At',
        updatedAt: 'Updated At',
        noData: 'No data available',
        previous: 'Previous',
        next: 'Next',
        active: 'Active',
        inactive: 'Inactive',
        notProvided: 'Not provided',
        notAvailable: 'Not available',
        back: 'Back',
        print: 'Print',
        order: 'Order',
        errorLoadingData: 'Error loading data',
        tryAgainLater: 'Please try again later',
        retry: 'Retry',
        by: 'by',
        pageInfo: 'Page {{current}} of {{total}}',
        amount: 'Amount',
        characters: 'characters',
        created: 'Created',
        owner: 'Owner',
        activate: 'Activate',
        deactivate: 'Deactivate',
        notes: 'Notes',
        totalAmount: 'Total Amount'
      },
      validation: {
        required: 'This field is required',
        positiveNumber: 'Must be a positive number',
        maxLength: 'Maximum {{max}} characters allowed',
        mustBeNumber: '{{field}} must be a number',
        invalidEmail: 'Invalid email format',
        invalidPhone: 'Invalid phone format',
        productNameRequired: 'Product name is required',
        invalidBarcode: 'Invalid barcode format (8-13 digits)',
        skuRequired: 'SKU is required',
        priceMustBePositive: 'Price must be positive',
        quantityMustBeInteger: 'Quantity must be a whole number',
        quantityCannotBeNegative: 'Quantity cannot be negative',
        amountMustBePositive: 'Amount must be positive',
        currencyRequired: 'Currency is required',
        invalidCurrency: 'Invalid currency',
        categoryNameRequired: 'Category name is required',
        brandNameRequired: 'Brand name is required',
        collectionNameRequired: 'Collection name is required',
        customerNameRequired: 'Customer name is required',
        addressRequired: 'Address is required',
        cityRequired: 'City is required',
        countryRequired: 'Country is required',
        postalCodeRequired: 'Postal code is required',
        mustBeFutureDate: 'Must be a future date',
        mustBePastDate: 'Must be a past date',
        atLeastOneRequired: 'At least one {{item}} is required',
        statusRequired: 'Status is required',
        invalidStatus: 'Invalid status',
        walletNameRequired: 'Wallet name is required',
        accountNumberRequired: 'Account number is required',
        invalidOrderNumber: 'Invalid order number format',
        discountMinimum: 'Discount cannot be negative',
        discountMaximum: 'Discount cannot exceed 100%',
        supplierNameRequired: 'Supplier name is required',
        paymentTermsMustBeInteger: 'Payment terms must be whole days',
        paymentTermsMinimum: 'Payment terms cannot be negative',
        paymentTermsMaximum: 'Payment terms cannot exceed 365 days',
        fieldsMustMatch: 'Fields must match',
        minArrayLength: '{{field}} must have at least {{min}} items',
        atLeastOneFieldRequired: 'At least one field must be filled',
        invalidDateRange: 'End date must be after start date'
      },
      product: {
        name: 'Product Name',
        description: 'Description',
        price: 'Price',
        category: 'Category',
        brand: 'Brand',
        sku: 'SKU',
        stock: 'Stock',
        status: 'Status',
        addProduct: 'Add Product',
        editProduct: 'Edit Product',
        productName: 'Product Name',
        productDescription: 'Product Description',
        quantity: 'Quantity'
      },
      products: {
        manageCatalog: 'Manage your product catalog',
        addProduct: 'Add Product',
        addProductDescription: 'Create a new product in the system',
        name: 'Product Name',
        sku: 'SKU',
        description: 'Description',
        price: 'Price',
        unitPrice: 'Unit Price',
        costPrice: 'Cost Price',
        weight: 'Weight (kg)',
        collections: 'Collections',
        optional: '(Optional)',
        category: 'Category',
        brand: 'Brand',
        reorderPoint: 'Reorder Point',
        enterName: 'Enter product name',
        enterSku: 'Enter SKU',
        enterDescription: 'Enter description',
        selectCategory: 'Select category',
        selectBrand: 'Select brand',
        selectCollection: 'Select collections...',
        noCollectionsFound: 'No collections found',
        noCategoriesFound: 'No categories found',
        noBrandsFound: 'No brands found',
        createProduct: 'Create Product',
        productCreated: 'Product created successfully',
        createError: 'Failed to create product',
        table: {
          columns: {
            name: 'Name',
            sku: 'SKU',
            category: 'Category',
            brand: 'Brand',
            price: 'Price',
            status: 'Status',
            actions: 'Actions'
          }
        }
        ,
        selectProductFromWarehouse: 'Select product from warehouse...',
        noProductsWithInventory: 'No products with inventory in this warehouse'
      },
      inventory: {
        stockLevel: 'Stock Level',
        lowStock: 'Low Stock',
        outOfStock: 'Out of Stock',
        reorderPoint: 'Reorder Point',
        lastUpdated: 'Last Updated',
        adjustStock: 'Adjust Stock',
        totalItems: 'Total Items',
        needsAttention: 'Needs Attention',
        urgentRestock: 'Urgent Restock',
        totalValue: 'Total Value',
        stockLevels: 'Stock Levels',
        warehouse: 'Warehouse',
        location: 'Location'
      },
      warehouses: {
        manageWarehouses: 'Manage your warehouse locations',
        main: 'Main Warehouse',
        secondary: 'Secondary Warehouse',
        distribution: 'Distribution Center',
        capacity: 'Capacity',
        management: 'Warehouse Management'
      },
      users: {
        total: 'Total Users',
        active: 'Active Users',
        admins: 'Administrators',
        pending: 'Pending Approval',
        activeRate: 'active rate',
        systemAdmins: 'System administrators',
        awaitingApproval: 'Awaiting approval',
        recentActivity: 'Recent Activity',
        loggedIn: 'Logged in',
        updatedProfile: 'Updated profile',
        passwordChanged: 'Password changed',
        roleDistribution: 'Role Distribution',
        warehouseManager: 'Warehouse Manager',
        inventoryClerk: 'Inventory Clerk',
        supervisor: 'Supervisor',
        admin: 'Administrator',
        management: 'User Management'
      },
      suppliers: {
        title: 'Suppliers',
        description: 'Manage your suppliers and their details',
        addSupplier: 'Add Supplier',
        empty: 'No suppliers found.',
        name: 'Name',
        contactPerson: 'Contact Person',
        phone: 'Phone',
        email: 'Email',
        balance: 'Balance',
        paymentTerms: 'Payment Terms (days)',
        country: 'Country',
        enterName: 'Enter supplier name',
        enterPhone: 'Enter phone number',
        searchPlaceholder: 'Search suppliers...'
      },
      logs: {
        exportLogs: 'Export Logs',
        totalToday: 'Total Today',
        errors: 'Errors',
        warnings: 'Warnings',
        activeUsers: 'Active Users',
        currentlyOnline: 'Currently online',
        recentActivity: 'Recent Activity',
        user: 'User',
        ip: 'IP Address',
        loadMore: 'Load More'
      },
      brands: {
        manageBrands: 'Manage your product brands',
        name: 'Name',
        description: 'Description',
        enterName: 'Enter brand name',
        enterDescription: 'Enter brand description',
        brandCreated: 'Brand created successfully',
        createError: 'Failed to create brand',
        addBrand: 'Add Brand',
        addBrandDescription: 'Create a new brand for your products',
        createBrand: 'Create Brand',
        searchPlaceholder: 'Search brands...',
        empty: 'No brands found.',
        columns: {
          name: 'Name',
          description: 'Description',
          status: 'Status',
          actions: 'Actions'
        },
        status: {
          active: 'Active',
          inactive: 'Inactive'
        }
      },
      categories: {
        manageCategories: 'Manage your product categories',
        name: 'Name',
        description: 'Description',
        enterName: 'Enter category name',
        enterDescription: 'Enter category description',
        categoryCreated: 'Category created successfully',
        createError: 'Failed to create category',
        addCategory: 'Add Category',
        addCategoryDescription: 'Create a new category for your products',
        createCategory: 'Create Category',
        searchPlaceholder: 'Search categories...',
        empty: 'No categories found.',
        columns: {
          name: 'Name',
          description: 'Description',
          status: 'Status',
          actions: 'Actions'
        },
        status: {
          active: 'Active',
          inactive: 'Inactive'
        }
      },
      collections: {
        manageCollections: 'Manage your product collections',
        name: 'Name',
        description: 'Description',
        enterName: 'Enter collection name',
        enterDescription: 'Enter description (optional)',
        collectionCreated: 'Collection created successfully',
        collectionUpdated: 'Collection updated successfully',
        createError: 'Failed to create collection',
        updateError: 'Failed to update collection',
        addCollection: 'Add Collection',
        editCollection: 'Edit Collection',
        addCollectionDescription: 'Create a new collection to group related products.',
        editCollectionDescription: 'Update the collection information below.',
        createCollection: 'Create Collection',
        updateCollection: 'Update Collection',
        searchPlaceholder: 'Search collections...',
        empty: 'No collections found.',
        columns: {
          name: 'Name',
          description: 'Description',
          status: 'Status',
          created: 'Created',
          actions: 'Actions'
        },
        status: {
          active: 'Active',
          inactive: 'Inactive'
        }
      },
      customers: {
        description: 'Manage your customers here.',
        addCustomer: 'Add Customer',
        editCustomer: 'Edit Customer',
        addCustomerDescription: 'Create a new customer profile.',
        editCustomerDescription: 'Update customer information.',
        createCustomer: 'Create Customer',
        updateCustomer: 'Update Customer',
        customerCreated: 'Customer created successfully.',
        customerUpdated: 'Customer updated successfully.',
        createError: 'Failed to create customer.',
        updateError: 'Failed to update customer.',
        deleteError: 'Failed to deactivate customer.',
        deleted: 'Customer deactivated successfully.',
        deleteTitle: 'Deactivate Customer',
        deleteConfirm: 'Are you sure you want to deactivate this customer? This customer will be hidden from active lists.',
        deactivate: 'Deactivate',
        searchPlaceholder: 'Search by name, email, or phone...',
        empty: 'No customers found.',
        noResults: 'No customers found matching your search.',
        enterName: 'Enter customer name',
        enterEmail: 'Enter email',
        enterPhone: 'Enter phone number',
        enterAddress: 'Enter address',
        enterCity: 'Enter city',
        enterCountry: 'Enter country',
        enterPostalCode: 'Enter postal code',
        phone: 'Phone',
        address: 'Address',
        city: 'City',
        country: 'Country',
        postalCode: 'Postal Code',
        details: {
          title: 'Customer Details',
          basicInformation: 'Basic Information',
          businessInformation: 'Business Information',
          name: 'Name',
          status: 'Status',
          email: 'Email',
          phone: 'Phone',
          city: 'City',
          country: 'Country',
          address: 'Address',
          customerId: 'Customer ID',
          discountPercentage: 'Discount Percentage',
          createdAt: 'Created At',
          lastUpdated: 'Last Updated'
        },
        status: {
          active: 'Active',
          inactive: 'Inactive'
        }
      },
      wallets: {
        dashboard: 'Wallet Dashboard',
        dashboardDescription: 'Manage your wallet balances and transactions',
        createWallet: 'Create Wallet',
        totalWallets: 'Total Wallets',
        active: 'active',
        defaultWallet: 'Default Wallet',
        noDefaultWallet: 'No default wallet',
        totalBalance: 'Total Balance',
        walletsInCurrency: 'wallets in currency',
        quickActions: 'Quick Actions',
        addFunds: 'Add Funds',
        withdrawFunds: 'Withdraw Funds',
        createFirstWallet: 'Create your first wallet to get started',
        myWallets: 'My Wallets',
        recentTransactions: 'Recent Transactions',
        noWalletsYet: 'No wallets yet',
        createFirstWalletDescription: 'Create your first wallet to start managing your finances',
        noTransactionsYet: 'No transactions yet',
        transactionsWillAppearHere: 'Your transactions will appear here',
        recentActivity: 'Recent Activity',
        recentActivityDescription: 'Latest transactions across all your wallets',
        activityAndHistory: 'Activity & History',
        selectWalletForHistory: 'Select a wallet to view transaction history',
        selectWalletForHistoryDescription: 'Click on a wallet from the wallets tab to see its detailed transaction history here',
        currencyCannotBeChanged: 'Currency cannot be changed after wallet creation',
        setDefault: 'Set Default',
        activateSuccess: 'Wallet activated successfully',
        deactivateSuccess: 'Wallet deactivated successfully',
        activateError: 'Failed to activate wallet',
        deactivateError: 'Failed to deactivate wallet',
        setDefaultSuccess: 'Default wallet updated successfully',
        setDefaultError: 'Failed to set default wallet',
        editWallet: 'Edit Wallet',
        editWalletDescription: 'Update wallet information',
        updateSuccess: 'Wallet updated successfully',
        updateError: 'Failed to update wallet',
        walletName: 'Wallet Name',
        accountNumber: 'Account Number',
        currency: 'Currency',
        balance: 'Balance',
        default: 'Default',
        setAsDefault: 'Set as Default',
        created: 'Created',
        owner: 'Owner',
        activate: 'Activate',
        deactivate: 'Deactivate',
        walletNamePlaceholder: 'Enter wallet name',
        walletNameDescription: 'A descriptive name for your wallet',
        selectCurrency: 'Select currency',
        currencyDescription: 'Choose the currency for this wallet',
        createWalletDescription: 'Create a new wallet to manage your funds',
        errorCreatingWallet: 'Failed to create wallet',
        walletCreatedSuccessfully: 'Wallet "{{name}}" has been created successfully',
        noWalletsFound: 'No wallets found',
        noActiveWallets: 'No active wallets found',
        selectWallet: 'Select wallet',
        casa: {
          title: 'Casa',
          treasury: 'Company Treasury',
          multiCurrency: 'Multi-Currency',
          badge: 'Casa'
        },
        userWallets: 'User Wallets',
        companyCasa: 'Company Casa',
        transactionCreated: 'Transaction Created',
        transactionCreatedSuccessfully: 'Transaction of {{amount}} {{currency}} has been recorded',
        errorCreatingTransaction: 'Failed to create transaction',
        addFundsDescription: 'Add funds to your wallet',
        withdrawFundsDescription: 'Withdraw funds from your wallet',
        transactionType: 'Transaction Type',
        incoming: 'Incoming',
        outgoing: 'Outgoing',
        amount: 'Amount',
        currentBalance: 'Current Balance',
        description: 'Description',
        required: 'Required',
        transactionDescriptionPlaceholder: 'Enter transaction description',
        outTransactionDescriptionPlaceholder: 'Enter reason for withdrawal',
        transactionDescriptionOptional: 'Provide details about this transaction',
        outTransactionDescriptionRequired: 'Please provide a reason for this withdrawal',
        insufficientFunds: 'Insufficient Funds',
        resultingBalance: 'Resulting Balance',
        balanceAfter: 'Balance After',
        referenceId: 'Reference ID',
        transactionHistory: 'Transaction History',
        relatedTransactions: 'Related Transactions',
        noTransactionsFound: 'No transactions found',
        availableBalance: 'Available Balance',
        wallet: 'Wallet',
        sendToCasa: {
          title: 'Send to Casa',
          description: 'Transfer money to the company casa account',
          button: 'Send to Casa',
          transfer: 'Transfer to Casa',
          quickAction: 'Send to Casa',
          descriptionPlaceholder: 'Enter reason for casa transfer...',
          previewTitle: 'Post-Transfer Balances',
          largeAmountWarning: 'You are about to transfer {{amount}}. Please confirm this action.',
          confirmTransfer: 'Confirm Transfer',
          submit: 'Send to Casa',
          processing: 'Processing...',
          success: 'Successfully transferred {{amount}} to casa',
          error: 'Failed to transfer money to casa'
        },
        errors: {
          insufficientFunds: 'Insufficient funds for this transfer'
        },
        confirmSetDefault: {
          title: 'Set Default Wallet',
          description: 'Are you sure you want to set "{{walletName}}" as your default wallet?'
        },
        confirmActivate: {
          title: 'Activate Wallet',
          description: 'Are you sure you want to activate "{{walletName}}"?'
        },
        confirmDeactivate: {
          title: 'Deactivate Wallet',
          description: 'Are you sure you want to deactivate "{{walletName}}"?'
        },
        viewDetails: 'View Details',
        walletsCount: '{{count}} wallet',
        walletsCount_plural: '{{count}} wallets',
        balanceSummary: 'Balance Summary',
        noWallets: 'No wallets found'
      },
      currency: {
        usd: 'US Dollar',
        uah: 'Ukrainian Hryvnia',
        eur: 'Euro'
      },
      messages: {
        productAdded: 'Product added successfully',
        productUpdated: 'Product updated successfully',
        productDeleted: 'Product deleted successfully',
        stockUpdated: 'Stock updated successfully'
      },
      addExpense: 'Add Expense',
      addSales: 'Add Sales',
      profile: 'Profile',
      settings: 'Settings',
      logout: 'Logout',
      expenses: {
        noExpensesFound: 'No expenses found. Click "Add Expense" to create your first expense record.'
      },
      payments: {
        errors: {
          invalidCustomerId: 'Invalid customer ID. Please refresh the page and try again.',
          invalidDestination: 'Invalid payment destination. Please select a wallet or Casa account again.',
          invalidAmount: 'Invalid payment amount. Please enter a valid amount.',
          invalidDestinationId: 'Invalid destination ID. Please select a wallet or Casa account again.',
          insufficientFunds: 'Insufficient funds for this payment.',
          paymentFailed: 'Payment failed. Please try again.'
        },
        customerPaymentSuccess: 'Customer payment processed successfully',
        customerPaymentError: 'Failed to process customer payment',
        supplierPaymentSuccess: 'Supplier payment processed successfully',
        supplierPaymentError: 'Failed to process supplier payment',
        paymentTooLarge: 'Payment Too Large',
        paymentExceedsLimit: 'Payment of {{payment}} exceeds the limit of {{max}} (2x current debt of {{current}})',
        largePaymentWarning: 'Large Payment',
        largePaymentInfo: 'You are about to make a payment of {{payment}} when the customer only owes {{debt}}. This will create a credit balance.',
        cashoutProcessed: 'Customer cashout processed successfully',
        cashoutFailed: 'Failed to process cashout',
        processCashout: 'Process Cashout',
        cashoutAmount: 'Cashout Amount',
        balanceAfter: 'Balance After',
        immediateProcessing: 'Immediate Processing',
        enterAmountToCashout: 'Enter amount to cashout',
        available: 'Available',
        enterReasonForCashout: 'Enter reason for cashout (e.g., Customer requested cash payment)',
        cashoutConfirmation: 'This will immediately process the cashout and reduce the customer\'s credit balance. This action cannot be undone.',
        cashoutProcess: 'Cashout Process',
        cashoutDescription: 'This action will immediately process the cashout and reduce the customer\'s credit balance. The source wallet will be automatically selected by the system.',
        processCashoutForCredit: 'Process Customer Cashout',
        processCashoutDescription: 'Process cashout for customer credit balance - partial amounts allowed',
        currentCreditBalance: 'Current Credit Balance',
        cashoutSummary: 'Cashout Summary'
      },
      transactions: {
        descriptions: {
          customerPayment: 'Customer {{customerName}} paid - {{description}}',
          paymentFromCustomer: 'Payment from customer {{customerName}}',
          orderPayment: 'Payment for order #{{orderId}}',
          orderPaymentFrom: 'Order #{{orderId}} payment from {{customerName}}',
          supplierPayment: 'Payment to supplier {{supplierName}}',
          supplierOrderPayment: 'Supplier {{supplierName}} payment for order #{{orderId}}',
          casaTransfer: 'Transfer to casa account',
          casaTransferWithReason: 'Casa transfer: {{reason}}',
          transferFromCasa: 'Transfer from casa to {{destination}}',
          walletTransfer: 'Transfer from {{fromWallet}} to {{toWallet}}',
          customerRefund: 'Refund to customer {{customerName}}',
          orderRefund: 'Refund for order #{{orderId}}',
          balanceAdjustment: 'Balance adjustment: {{reason}}',
          writeOff: 'Write-off: {{reason}}',
          expense: 'Expense: {{description}}',
          companyExpense: 'Company expense for {{purpose}}',
          currencyConversion: 'Currency conversion from {{fromCurrency}} to {{toCurrency}}',
          ordersSettled: 'Settled {{orderCount}} orders: {{orderNumbers}}',
          fundsAdded: 'Funds added: {{description}}',
          withdrawal: 'Withdrawal: {{reason}}'
        },
        types: {
          in: 'Incoming',
          out: 'Outgoing',
          payment: 'Payment',
          refund: 'Refund',
          adjustment: 'Adjustment',
          transfer: 'Transfer'
        },
        status: {
          pending: 'Pending',
          completed: 'Completed',
          failed: 'Failed',
          cancelled: 'Cancelled',
          processing: 'Processing'
        },
        paymentMethods: {
          cash: 'Cash',
          card: 'Card',
          bank_transfer: 'Bank Transfer',
          wallet: 'Wallet',
          casa: 'Casa'
        },
        references: {
          order: 'Order #{{id}}',
          customer: 'Customer #{{id}}',
          supplier: 'Supplier #{{id}}',
          invoice: 'Invoice #{{id}}'
        }
      },
      time: {
        justNow: 'just now',
        secondAgo: '1 second ago',
        secondsAgo: '{{count}} seconds ago',
        minuteAgo: '1 minute ago',
        minutesAgo: '{{count}} minutes ago',
        hourAgo: '1 hour ago',
        hoursAgo: '{{count}} hours ago',
        yesterday: 'yesterday',
        dayAgo: '1 day ago',
        daysAgo: '{{count}} days ago',
        weekAgo: '1 week ago',
        weeksAgo: '{{count}} weeks ago',
        monthAgo: '1 month ago',
        monthsAgo: '{{count}} months ago',
        yearAgo: '1 year ago',
        yearsAgo: '{{count}} years ago'
      }
    }
  },
  uk: {
    translation: {
      history: {
        PAYMENT_RECEIVED_FROM_CUSTOMER: 'Отримано платіж {{amount}} {{currency}} від {{customerName}}',
        ORDER_DEBT_CREATED: 'Створено борг {{amount}} {{currency}} для замовлення №{{orderNumber}}',
        CUSTOMER_REFUND_ISSUED: 'Видано повернення {{amount}} {{currency}} для {{customerName}} - {{reason}}',
        CUSTOMER_REFUND_CREDIT_APPLIED: 'Застосовано кредит повернення {{amount}} {{currency}} для {{customerName}}',
        CUSTOMER_CASHOUT_PROCESSED: 'Оброблено вивід коштів {{amount}} {{currency}} для {{customerName}}',
        CUSTOMER_BALANCE_MANUAL_ADJUSTMENT: 'Ручне коригування {{amount}} {{currency}} для {{customerName}} виконав {{performedBy}}',
        CUSTOMER_BALANCE_ADJUSTMENT: 'Коригування балансу {{amount}} {{currency}} для {{customerName}}',
        WALLET_TRANSFER_SENT: 'Переказано {{amount}} {{currency}} на {{toWalletName}}',
        WALLET_TRANSFER_RECEIVED: 'Отримано {{amount}} {{currency}} від {{fromWalletName}}',
        WALLET_CASA_TRANSFER_SENT: 'Переказано {{amount}} {{currency}} на каса рахунок',
        WALLET_CASA_TRANSFER_RECEIVED: 'Отримано {{amount}} {{currency}} від {{fromWalletName}}',
        WALLET_DEPOSIT_COMPLETED: 'Поповнено {{amount}} {{currency}} на {{walletName}}',
        WALLET_WITHDRAW_COMPLETED: 'Знято {{amount}} {{currency}} з {{walletName}}',
        WALLET_CASH_DEPOSIT: 'Готівкове поповнення {{amount}} {{currency}} на {{walletName}}',
        WALLET_CASH_TRANSFER: 'Готівковий переказ {{amount}} {{currency}} в касу',
      },
      nav: {
        dashboard: 'Панель управління',
        orders: 'Замовлення',
        products: 'Товари',
        categories: 'Категорії',
        brands: 'Бренди',
        collections: 'Колекції',
        customers: 'Клієнти',
        'product-configuration': 'Налаштування товарів',
        inventory: 'Інвентар',
        warehouses: 'Склади',
        wallets: 'Гаманці',
        treasury: 'Казна Компанії',
        'casa-wallet': 'Каса Гаманець',
        users: 'Користувачі',
        logs: 'Журнали',
        logout: 'Вийти',
        brand: 'Бренд',
        category: 'Категорія',
        collection: 'Колекція',
        customer: 'Клієнт',
        user: 'Користувач',
        warehouse: 'Склад'
      },
      auth: {
        login: 'Вхід',
        email: 'Електронна пошта',
        password: 'Пароль',
        loginButton: 'Увійти',
        loginError: 'Невірна електронна пошта або пароль',
        loading: 'Вхід...',
        welcome: 'Ласкаво просимо до Warehouse Ukraine',
        loginSubtitle: 'Введіть свої дані для доступу до системи',
        toggleLanguage: 'English'
      },
      orders: {
        manageTrack: 'Керуйте та відстежуйте всі замовлення',
        newOrder: 'Нове замовлення',
        searchPlaceholder: 'Пошук замовлень...',
        selectAll: 'Вибрати всі на сторінці',
        view: 'Перегляд',
        process: 'Обробити',
        empty: 'Замовлення не знайдено',
        addExpense: 'Додати витрати',
        addSales: 'Додати продажі',
        addExpenseDescription: 'Записати нову витратну операцію',
        addSalesDescription: 'Записати нову операцію продажу',
        toolbar: {
          search: 'Пошук замовлень...',
          dateRange: 'Вибрати діапазон дат',
          status: 'Фільтр за статусом',
          type: 'Фільтр за типом',
          warehouse: 'Фільтр за складом',
          allWarehouses: 'Всі склади',
          columns: 'Видимість стовпців',
          density: 'Щільність таблиці',
          compact: 'Щільний',
          comfortable: 'Зручний'
        },
        selection: {
          count: 'Вибрано {{count}} замовлень',
          allMatching: 'Вибрано всі {{count}} замовлень за фільтром',
          selectAllMatching: 'Вибрати всі {{total}} замовлень за фільтром',
          clear: 'Очистити вибір'
        },
        bulk: {
          actions: 'Масові дії',
          updateStatus: 'Оновити статус',
          assignWarehouse: 'Призначити склад',
          print: 'Друкувати замовлення',
          cancel: 'Скасувати замовлення',
          success: 'Масову дію виконано успішно',
          error: 'Не вдалося виконати масову дію'
        },
        actions: {
          view: 'Переглянути деталі',
          process: 'Обробити замовлення',
          print: 'Друкувати замовлення',
          cancel: 'Скасувати замовлення'
        },
        detail: {
          orderActivity: 'Активність замовлення',
          noActivity: 'Активність не зафіксована',
          orderCreated: 'Замовлення створено',
          orderUpdated: 'Замовлення оновлено',
          orderCompleted: 'Замовлення завершено',
          orderCancelled: 'Замовлення скасовано',
          orderRefunded: 'Замовлення повернено',
          orderProcessingFinished: 'Обробка замовлення успішно завершена',
          orderDetailsModified: 'Деталі замовлення були змінені',
          salesRepresentative: 'Торговий представник',
          payment: 'Платіж',
          seller: 'Продавець',
          orderNotes: 'Примітки до замовлення',
          refundReason: 'Причина повернення',
          cancellationReason: 'Причина скасування',
          updateSuccessTitle: 'Замовлення успішно оновлено',
          updateSuccessDescription: 'Замовлення №{{orderNumber}} було оновлено.',
          updateSuccessDescriptionWithChanges: 'Замовлення №{{orderNumber}} було оновлено (Оновлено: {{fields}}).',
          updateErrorTitle: 'Не вдалося оновити',
          updateErrorDescription: 'Не вдалося оновити замовлення. Спробуйте ще раз.',
          orderNotFound: 'Замовлення не знайдено',
          backToOrders: 'Повернутися до замовлень',
          editOrder: 'Редагувати замовлення',
          recordPayment: 'Записати оплату',
          paymentStatus: 'Статус',
          paymentStatusLabels: {
            PAID: 'Оплачено',
            PARTIAL: 'Частково оплачено',
            UNPAID: 'Не оплачено'
          },
          paymentProgressPaid: 'Оплачено: {{percent}}%',
          paymentProgressRemaining: 'Залишок: {{amount}}',
          remainingAmountLabel: 'Залишок:',
          paidAmountLabel: 'Оплачено:',
          discountApplied: '{{percent}}% знижки застосовано',
          systemUser: 'система',
          fallbackCreatedDescription: 'Замовлення №{{orderNumber}} було створене {{createdBy}}',
          fallbackPaymentProcessedTitle: 'Оплату оброблено',
          fallbackPaymentProcessedDescription: 'Оплату на суму {{amount}} успішно оброблено.',
          fallbackOrderModifiedDescription: 'Деталі замовлення були змінені.',
          fallbackOrderCompletedDescription: 'Замовлення успішно завершено. {{itemCount}} товарів оброблено.',
          fallbackOrderCancelledDescription: 'Замовлення було скасовано.',
          fallbackPendingTitle: 'Очікує обробки',
          fallbackPendingDescription: 'Замовлення очікує на завершення.'
        },
        activityTitles: {
          ORDER_CREATED: 'Створено замовлення',
          ORDER_STATUS_CHANGED: 'Змінено статус',
          ORDER_CANCELLED: 'Замовлення скасовано',
          ORDER_UPDATED: 'Замовлення оновлено',
          ORDER_ITEM_ADDED: 'Додано товар',
          ORDER_ITEM_REMOVED: 'Вилучено товар',
          ORDER_DEBT_CREATED: 'Зафіксовано борг',
          ORDER_DEBT_INCREASED: 'Борг збільшено',
          ORDER_DEBT_DECREASED: 'Борг зменшено'
        },
        activityDescriptions: {
          ORDER_CREATED: 'Замовлення №{{orderNumber}} створено для {{customerName}} ({{itemCount}} позицій).',
          ORDER_STATUS_CHANGED: 'Статус змінено з {{oldStatusLabel}} на {{newStatusLabel}}.',
          ORDER_CANCELLED: 'Замовлення скасовано.',
          ORDER_UPDATED: 'Замовлення оновлено.',
          ORDER_ITEM_ADDED: 'Додано {{quantity}} × {{productName}} до замовлення.',
          ORDER_ITEM_REMOVED: 'Вилучено {{quantity}} × {{productName}} із замовлення.',
          ORDER_DEBT_CREATED: 'Борг на суму {{amountLabel}} зафіксовано для цього замовлення.',
          ORDER_DEBT_INCREASED: 'Борг збільшено на {{amountDeltaLabel}}.',
          ORDER_DEBT_DECREASED: 'Борг зменшено на {{amountDeltaLabel}}.',
          ORDER_CREDIT_APPLIED: 'Кредит клієнта {{amountLabel}} автоматично застосовано до замовлення.',
          PAYMENT_RECEIVED: 'Отримано оплату {{amountLabel}} від клієнта.',
          ORDER_PAYMENT_ADDED: 'До замовлення додано оплату {{amountLabel}}.'
        },
        activityLabels: {
          reason: 'Причина',
          reference: 'Референс',
          amountIncreased: 'Сума збільшена на {{amount}}',
          amountDecreased: 'Сума зменшена на {{amount}}',
          creditApplied: 'Застосовано кредит',
          creditAppliedDetail: '💳 Застосовано кредит: {{amount}}',
          remainingAmount: 'Залишок',
          remainingAmountDetail: '📋 Залишок: {{amount}}',
          fullyPaid: 'Повністю оплачено',
          fullyPaidDetail: '✅ Повністю оплачено',
          outstandingDebt: 'Заборгованість',
          unset: 'Не вказано'
        },
        pagination: {
          showing: 'Показано {{start}} з {{end}} з {{total}} замовлень',
          page: 'Сторінка {{current}} з {{total}}'
        },
        status: {
          all: 'Усі статуси',
          draft: 'Чернетка',
          pending: 'В очікуванні',
          completed: 'Завершено',
          cancelled: 'Скасовано',
          refunded: 'Повернено',
          partially_refunded: 'Частково повернено',
          PENDING: 'В очікуванні',
          COMPLETED: 'Завершено',
          CANCELLED: 'Скасовано',
          REFUNDED: 'Повернено',
          PARTIALLY_REFUNDED: 'Частково повернено',
          DRAFT: 'Чернетка'
        },
        type: 'Тип',
        types: {
          all: 'Усі типи',
          SALE: 'Продаж',
          RETURN: 'Повернення',
          EXCHANGE: 'Обмін',
        },
        columns: {
          order: 'Замовлення',
          date: 'Дата',
          customer: 'Клієнт',
          status: 'Статус',
          type: 'Тип',
          items: 'Позиції',
          warehouse: 'Склад',
          total: 'Сума',
          actions: 'Дії'
        },
        customer: 'Клієнт',
        warehouse: 'Склад',
        selectCustomer: 'Оберіть клієнта',
        selectWarehouse: 'Оберіть склад',
        selectProduct: 'Оберіть товар',
        noCustomersFound: 'Клієнтів не знайдено',
        noWarehousesFound: 'Складів не знайдено',
        noProductsFound: 'Товарів не знайдено',
        discountPercentage: 'Відсоток знижки',
        items: 'Позиції замовлення',
        item: 'Позиція',
        addItem: 'Додати позицію',
        product: 'Товар',
        quantity: 'Кількість',
        unitPrice: 'Ціна за одиницю',
        itemNotes: 'Примітки до позиції',
        enterItemNotes: 'Введіть примітки до позиції',
        notes: 'Примітки до замовлення',
        enterNotes: 'Введіть примітки до замовлення',
        notesPlaceholder: 'Примітки до замовлення...',
        discountPercent: 'Знижка %',
        summary: 'Підсумок замовлення',
        subtotal: 'Підсума',
        discount: 'Знижка',
        total: 'Всього',
        saveChanges: 'Зберегти зміни',
        createOrder: 'Створити замовлення',
        orderCreated: 'Замовлення успішно створено',
        createError: 'Не вдалося створити замовлення'
      },
      common: {
        search: 'Пошук',
        add: 'Додати',
        edit: 'Редагувати',
        delete: 'Видалити',
        save: 'Зберегти',
        cancel: 'Скасувати',
        loading: 'Завантаження...',
        error: 'Помилка', 
        success: 'Успіх',
        loadingCategories: 'Завантаження категорій...',
        loadingBrands: 'Завантаження брендів...',
        loadingCollections: 'Завантаження колекцій...',
        selectStatus: 'Оберіть статус',
        selectReadiness: 'Оберіть готовність',
        selectCategories: 'Оберіть категорії...',
        selectBrands: 'Оберіть бренди...',
        selectCollections: 'Оберіть колекції...',
        selectProduct: 'Оберіть товар...',
        selectWarehouse: 'Оберіть склад...',
        selectCustomer: 'Оберіть клієнта...',
        selectSupplier: 'Оберіть постачальника...',
        selectExpenseType: 'Оберіть тип витрат',
        selectWallet: 'Оберіть гаманець',
        selectCategory: 'Оберіть категорію',
        selectBrand: 'Оберіть бренд',
        noCategoriesFound: 'Категорії не знайдено',
        noBrandsFound: 'Бренди не знайдено',
        noCollectionsFound: 'Колекції не знайдено',
        noProductsFound: 'Товари не знайдено',
        noWarehousesFound: 'Склади не знайдено',
        noCustomersFound: 'Клієнти не знайдено',
        noSuppliersFound: 'Постачальники не знайдено',
        noProductsAvailable: 'Товари недоступні',
        noItemsFound: 'Елементи не знайдено',
        noActionsAvailable: 'Дії недоступні',
        enterProductName: 'Введіть назву товару',
        enterSku: 'Введіть артикул',
        enterDescription: 'Введіть опис',
        enterExpenseDescription: 'Введіть опис витрат',
        enterReceiptNumber: 'Введіть номер квитанції/посилання',
        enterTrackingNumber: 'Введіть номер відстеження...',
        enterShippingAddress: 'Введіть адресу доставки...',
        optional: 'Необов\'язково',
        comingSoon: 'Незабаром',
        management: 'Управління',
        filters: 'Фільтри',
        export: 'Експорт',
        items: 'Елементи',
        staff: 'Персонал',
        used: 'Використано',
        fromLastMonth: 'з минулого місяця',
        thisMonth: 'цього місяця',
        fromYesterday: 'з вчора',
        hoursAgo: 'годин тому',
        dayAgo: 'день тому',
        filter: 'Фільтр',
        actions: 'Дії',
        name: 'Назва',
        description: 'Опис',
        status: 'Статус',
        createdAt: 'Створено',
        updatedAt: 'Оновлено',
        noData: 'Дані відсутні',
        previous: 'Попередня',
        next: 'Наступна',
        back: 'Назад',
        print: 'Друк',
        order: 'Замовлення',
        errorLoadingData: 'Помилка завантаження даних',
        tryAgainLater: 'Спробуйте ще раз пізніше',
        retry: 'Повторити',
        by: 'від',
        pageInfo: 'Сторінка {{current}} з {{total}}',
        amount: 'Сума',
        characters: 'символів',
        created: 'Створено',
        owner: 'Власник',
        activate: 'Активувати',
        deactivate: 'Деактивувати',
        notes: 'Примітки',
        totalAmount: 'Загальна сума'
      },
      validation: {
        required: 'Це поле обов\'язкове',
        positiveNumber: 'Має бути позитивним числом',
        maxLength: 'Максимум {{max}} символів дозволено',
        mustBeNumber: '{{field}} має бути числом',
        invalidEmail: 'Недійсний формат електронної пошти',
        invalidPhone: 'Недійсний формат телефону',
        productNameRequired: 'Назва товару обов\'язкова',
        invalidBarcode: 'Недійсний формат штрих-коду (8-13 цифр)',
        skuRequired: 'Артикул обов\'язковий',
        priceMustBePositive: 'Ціна має бути додатною',
        quantityMustBeInteger: 'Кількість має бути цілим числом',
        quantityCannotBeNegative: 'Кількість не може бути від\'ємною',
        amountMustBePositive: 'Сума має бути додатною',
        currencyRequired: 'Валюта обов\'язкова',
        invalidCurrency: 'Недійсна валюта',
        categoryNameRequired: 'Назва категорії обов\'язкова',
        brandNameRequired: 'Назва бренду обов\'язкова',
        collectionNameRequired: 'Назва колекції обов\'язкова',
        customerNameRequired: 'Ім\'я клієнта обов\'язкове',
        addressRequired: 'Адреса обов\'язкова',
        cityRequired: 'Місто обов\'язкове',
        countryRequired: 'Країна обов\'язкова',
        postalCodeRequired: 'Поштовий індекс обов\'язковий',
        mustBeFutureDate: 'Має бути майбутня дата',
        mustBePastDate: 'Має бути минула дата',
        atLeastOneRequired: 'Принаймні один {{item}} обов\'язковий',
        statusRequired: 'Статус обов\'язковий',
        invalidStatus: 'Недійсний статус',
        walletNameRequired: 'Назва гаманця обов\'язкова',
        accountNumberRequired: 'Номер рахунку обов\'язковий',
        invalidOrderNumber: 'Недійсний формат номера замовлення',
        discountMinimum: 'Знижка не може бути від\'ємною',
        discountMaximum: 'Знижка не може перевищувати 100%',
        supplierNameRequired: 'Назва постачальника обов\'язкова',
        paymentTermsMustBeInteger: 'Термін оплати має бути цілими днями',
        paymentTermsMinimum: 'Термін оплати не може бути від\'ємним',
        paymentTermsMaximum: 'Термін оплати не може перевищувати 365 днів',
        fieldsMustMatch: 'Поля мають співпадати',
        minArrayLength: '{{field}} має містити принаймні {{min}} елементів',
        atLeastOneFieldRequired: 'Принаймні одне поле має бути заповнене',
        invalidDateRange: 'Кінцева дата має бути після початкової'
      },
      product: {
        name: 'Назва товару',
        description: 'Опис',
        price: 'Ціна',
        category: 'Категорія',
        brand: 'Бренд',
        sku: 'Артикул',
        stock: 'Запас',
        status: 'Статус',
        addProduct: 'Додати товар',
        editProduct: 'Редагувати товар',
        productName: 'Назва товару',
        productDescription: 'Опис товару',
        quantity: 'Кількість'
      },
      products: {
        manageCatalog: 'Керуйте каталогом товарів',
        addProduct: 'Додати товар',
        addProductDescription: 'Створити новий товар в системі',
        name: 'Назва товару',
        sku: 'Артикул',
        description: 'Опис',
        price: 'Ціна',
        unitPrice: 'Ціна за одиницю',
        costPrice: 'Собівартість',
        weight: 'Вага (кг)',
        collections: 'Колекції',
        optional: '(Необов\'язково)',
        category: 'Категорія',
        brand: 'Бренд',
        reorderPoint: 'Точка замовлення',
        enterName: 'Введіть назву товару',
        enterSku: 'Введіть артикул',
        enterDescription: 'Введіть опис',
        selectCategory: 'Оберіть категорію',
        selectBrand: 'Оберіть бренд',
        selectCollection: 'Оберіть колекції...',
        noCollectionsFound: 'Колекцій не знайдено',
        noCategoriesFound: 'Категорій не знайдено',
        noBrandsFound: 'Брендів не знайдено',
        createProduct: 'Створити товар',
        productCreated: 'Товар успішно створено',
        createError: 'Не вдалося створити товар',
        table: {
          columns: {
            name: 'Назва',
            sku: 'Артикул',
            category: 'Категорія',
            brand: 'Бренд',
            price: 'Ціна',
            status: 'Статус',
            actions: 'Дії'
          }
        }
        ,
        selectProductFromWarehouse: 'Оберіть товар зі складу...',
        noProductsWithInventory: 'Немає товарів з наявністю на цьому складі'
      },
      inventory: {
        stockLevel: 'Рівень запасів',
        lowStock: 'Низький запас',
        outOfStock: 'Немає в наявності',
        reorderPoint: 'Точка замовлення',
        lastUpdated: 'Останнє оновлення',
        adjustStock: 'Коригувати запас',
        totalItems: 'Всього товарів',
        needsAttention: 'Потребує уваги',
        urgentRestock: 'Терміново поповнити',
        totalValue: 'Загальна вартість',
        stockLevels: 'Рівні запасів',
        warehouse: 'Склад',
        location: 'Місцезнаходження'
      },
      warehouses: {
        manageWarehouses: 'Керуйте складськими приміщеннями',
        main: 'Головний склад',
        secondary: 'Вторинний склад',
        distribution: 'Розподільчий центр',
        capacity: 'Місткість',
        management: 'Управління складами'
      },
      users: {
        total: 'Всього користувачів',
        active: 'Активні користувачі',
        admins: 'Адміністратори',
        pending: 'Очікують схвалення',
        activeRate: 'рівень активності',
        systemAdmins: 'Системні адміністратори',
        awaitingApproval: 'Очікують схвалення',
        recentActivity: 'Недавня активність',
        loggedIn: 'Увійшов в систему',
        updatedProfile: 'Оновив профіль',
        passwordChanged: 'Змінив пароль',
        roleDistribution: 'Розподіл ролей',
        warehouseManager: 'Менеджер складу',
        inventoryClerk: 'Працівник інвентаризації',
        supervisor: 'Супервайзер',
        admin: 'Адміністратор',
        management: 'Управління користувачами'
      },
      logs: {
        exportLogs: 'Експорт журналів',
        totalToday: 'Всього сьогодні',
        errors: 'Помилки',
        warnings: 'Попередження',
        activeUsers: 'Активні користувачі',
        currentlyOnline: 'Зараз онлайн',
        recentActivity: 'Недавня активність',
        user: 'Користувач',
        ip: 'IP-адреса',
        loadMore: 'Завантажити більше'
      },
      brands: {
        manageBrands: 'Керуйте брендами товарів',
        name: 'Назва',
        description: 'Опис',
        enterName: 'Введіть назву бренду',
        enterDescription: 'Введіть опис бренду',
        brandCreated: 'Бренд успішно створено',
        createError: 'Не вдалося створити бренд',
        addBrand: 'Додати бренд',
        addBrandDescription: 'Створити новий бренд для ваших товарів',
        createBrand: 'Створити бренд',
        searchPlaceholder: 'Пошук брендів...',
        empty: 'Бренди не знайдено.',
        columns: {
          name: 'Назва',
          description: 'Опис',
          status: 'Статус',
          actions: 'Дії'
        },
        status: {
          active: 'Активний',
          inactive: 'Неактивний'
        }
      },
      categories: {
        manageCategories: 'Керуйте категоріями товарів',
        name: 'Назва',
        description: 'Опис',
        enterName: 'Введіть назву категорії',
        enterDescription: 'Введіть опис категорії',
        categoryCreated: 'Категорію успішно створено',
        createError: 'Не вдалося створити категорію',
        addCategory: 'Додати категорію',
        addCategoryDescription: 'Створити нову категорію для ваших товарів',
        createCategory: 'Створити категорію',
        searchPlaceholder: 'Пошук категорій...',
        empty: 'Категорії не знайдено.',
        columns: {
          name: 'Назва',
          description: 'Опис',
          status: 'Статус',
          actions: 'Дії'
        },
        status: {
          active: 'Активна',
          inactive: 'Неактивна'
        }
      },
      collections: {
        manageCollections: 'Керуйте колекціями товарів',
        name: 'Назва',
        description: 'Опис',
        enterName: 'Введіть назву колекції',
        enterDescription: 'Введіть опис (необов\'язково)',
        collectionCreated: 'Колекцію успішно створено',
        collectionUpdated: 'Колекцію успішно оновлено',
        createError: 'Не вдалося створити колекцію',
        updateError: 'Не вдалося оновити колекцію',
        addCollection: 'Додати колекцію',
        editCollection: 'Редагувати колекцію',
        addCollectionDescription: 'Створити нову колекцію для групування пов\'язаних товарів.',
        editCollectionDescription: 'Оновити інформацію про колекцію нижче.',
        createCollection: 'Створити колекцію',
        updateCollection: 'Оновити колекцію',
        searchPlaceholder: 'Пошук колекцій...',
        empty: 'Колекції не знайдено.',
        columns: {
          name: 'Назва',
          description: 'Опис',
          status: 'Статус',
          created: 'Створено',
          actions: 'Дії'
        },
        status: {
          active: 'Активна',
          inactive: 'Неактивна'
        }
      },
      customers: {
        description: 'Керуйте своїми клієнтами тут.',
        addCustomer: 'Додати клієнта',
        editCustomer: 'Редагувати клієнта',
        addCustomerDescription: 'Створити новий профіль клієнта.',
        editCustomerDescription: 'Оновити інформацію про клієнта.',
        createCustomer: 'Створити клієнта',
        updateCustomer: 'Оновити клієнта',
        customerCreated: 'Клієнта успішно створено.',
        customerUpdated: 'Клієнта успішно оновлено.',
        createError: 'Не вдалося створити клієнта.',
        updateError: 'Не вдалося оновити клієнта.',
        deleteError: 'Не вдалося деактивувати клієнта.',
        deleted: 'Клієнта успішно деактивовано.',
        deleteTitle: 'Деактивувати клієнта',
        deleteConfirm: 'Ви впевнені, що хочете деактивувати цього клієнта? Цей клієнт буде приховано з активних списків.',
        deactivate: 'Деактивувати',
        searchPlaceholder: 'Пошук за іменем, електронною поштою або телефоном...',
        empty: 'Клієнтів не знайдено.',
        noResults: 'Клієнтів за вашим запитом не знайдено.',
        enterName: 'Введіть ім\'я клієнта',
        enterEmail: 'Введіть електронну пошту',
        enterPhone: 'Введіть номер телефону',
        enterAddress: 'Введіть адресу',
        enterCity: 'Введіть місто',
        enterCountry: 'Введіть країну',
        enterPostalCode: 'Введіть поштовий індекс',
        phone: 'Телефон',
        address: 'Адреса',
        city: 'Місто',
        country: 'Країна',
        postalCode: 'Поштовий індекс',
        details: {
          title: 'Деталі клієнта',
          basicInformation: 'Основна інформація',
          businessInformation: 'Бізнес інформація',
          name: 'Ім\'я',
          status: 'Статус',
          email: 'Електронна пошта',
          phone: 'Телефон',
          city: 'Місто',
          country: 'Країна',
          address: 'Адреса',
          customerId: 'ID клієнта',
          discountPercentage: 'Відсоток знижки',
          createdAt: 'Створено',
          lastUpdated: 'Останнє оновлення'
        },
        status: {
          active: 'Активний',
          inactive: 'Неактивний'
        }
      },
      wallets: {
        dashboard: 'Панель керування гаманцями',
        dashboardDescription: 'Керуйте балансами гаманців та транзакціями',
        createWallet: 'Створити гаманець',
        totalWallets: 'Всього гаманців',
        active: 'активний',
        defaultWallet: 'Основний гаманець',
        noDefaultWallet: 'Немає основного гаманця',
        totalBalance: 'Загальний баланс',
        walletsInCurrency: 'гаманці в валюті',
        quickActions: 'Швидкі дії',
        addFunds: 'Додати кошти',
        withdrawFunds: 'Зняти кошти',
        createFirstWallet: 'Створіть свій перший гаманець для початку',
        myWallets: 'Мої гаманці',
        recentTransactions: 'Останні транзакції',
        noWalletsYet: 'Гаманців ще немає',
        createFirstWalletDescription: 'Створіть свій перший гаманець для керування фінансами',
        noTransactionsYet: 'Транзакцій ще немає',
        transactionsWillAppearHere: 'Ваші транзакції з\'являться тут',
        recentActivity: 'Недавня активність',
        recentActivityDescription: 'Останні транзакції по всіх ваших гаманцях',
        activityAndHistory: 'Активність і історія',
        selectWalletForHistory: 'Оберіть гаманець для перегляду історії транзакцій',
        selectWalletForHistoryDescription: 'Клікніть на гаманець на вкладці гаманців, щоб побачити детальну історію транзакцій тут',
        currencyCannotBeChanged: 'Валюту не можна змінити після створення гаманця',
        setDefault: 'Встановити за замовчуванням',
        activateSuccess: 'Гаманець успішно активовано',
        deactivateSuccess: 'Гаманець успішно деактивовано',
        activateError: 'Не вдалося активувати гаманець',
        deactivateError: 'Не вдалося деактивувати гаманець',
        setDefaultSuccess: 'Основний гаманець успішно оновлено',
        setDefaultError: 'Не вдалося встановити основний гаманець',
        editWallet: 'Редагувати гаманець',
        editWalletDescription: 'Оновити інформацію про гаманець',
        updateSuccess: 'Гаманець успішно оновлено',
        updateError: 'Не вдалося оновити гаманець',
        walletName: 'Назва гаманця',
        accountNumber: 'Номер рахунку',
        currency: 'Валюта',
        balance: 'Баланс',
        default: 'Основний',
        setAsDefault: 'Встановити як основний',
        created: 'Створено',
        owner: 'Власник',
        activate: 'Активувати',
        deactivate: 'Деактивувати',
        walletNamePlaceholder: 'Введіть назву гаманця',
        walletNameDescription: 'Описова назва для вашого гаманця',
        selectCurrency: 'Оберіть валюту',
        currencyDescription: 'Виберіть валюту для цього гаманця',
        createWalletDescription: 'Створіть новий гаманець для керування коштами',
        errorCreatingWallet: 'Не вдалося створити гаманець',
        walletCreatedSuccessfully: 'Гаманець "{{name}}" успішно створено',
        noWalletsFound: 'Гаманці не знайдено',
        noActiveWallets: 'Активних гаманців не знайдено',
        selectWallet: 'Оберіть гаманець',
        casa: {
          title: 'Каса',
          treasury: 'Казна Компанії',
          multiCurrency: 'Підтримка Мульти-валют',
          badge: 'Каса'
        },
        userWallets: 'Гаманці користувача',
        companyCasa: 'Каса компанії',
        transactionCreated: 'Транзакцію створено',
        transactionCreatedSuccessfully: 'Транзакцію на {{amount}} {{currency}} записано',
        errorCreatingTransaction: 'Не вдалося створити транзакцію',
        addFundsDescription: 'Додати кошти до гаманця',
        withdrawFundsDescription: 'Зняти кошти з гаманця',
        transactionType: 'Тип транзакції',
        incoming: 'Надходження',
        outgoing: 'Списання',
        amount: 'Сума',
        currentBalance: 'Поточний баланс',
        description: 'Опис',
        required: 'Обов\'язково',
        transactionDescriptionPlaceholder: 'Введіть опис транзакції',
        outTransactionDescriptionPlaceholder: 'Введіть причину зняття',
        transactionDescriptionOptional: 'Надайте деталі про цю транзакцію',
        outTransactionDescriptionRequired: 'Будь ласка, вкажіть причину зняття',
        insufficientFunds: 'Недостатньо коштів',
        resultingBalance: 'Підсумковий баланс',
        balanceAfter: 'Баланс після',
        referenceId: 'ID посилання',
        transactionHistory: 'Історія транзакцій',
        relatedTransactions: 'Пов\'язані транзакції',
        noTransactionsFound: 'Транзакції не знайдено',
        availableBalance: 'Доступний баланс',
        wallet: 'Гаманець',
        sendToCasa: {
          title: 'Переказати до каси',
          description: 'Перевести гроші на рахунок каси компанії',
          button: 'Переказати до каси',
          transfer: 'Переказ до каси',
          quickAction: 'Переказати до каси',
          descriptionPlaceholder: 'Введіть причину переказу до каси...',
          previewTitle: 'Баланси після переказу',
          largeAmountWarning: 'Ви збираєтесь перевести {{amount}}. Будь ласка, підтвердіть цю дію.',
          confirmTransfer: 'Підтвердити переказ',
          submit: 'Переказати до каси',
          processing: 'Обробка...',
          success: 'Успішно переведено {{amount}} до каси',
          error: 'Не вдалося перевести гроші до каси'
        },
        errors: {
          insufficientFunds: 'Недостатньо коштів для цього переказу'
        },
        confirmSetDefault: {
          title: 'Встановити основний гаманець',
          description: 'Ви впевнені, що хочете встановити "{{walletName}}" як основний гаманець?'
        },
        confirmActivate: {
          title: 'Активувати гаманець',
          description: 'Ви впевнені, що хочете активувати "{{walletName}}"?'
        },
        confirmDeactivate: {
          title: 'Деактивувати гаманець',
          description: 'Ви впевнені, що хочете деактивувати "{{walletName}}"?'
        },
        viewDetails: 'Переглянути деталі',
        walletsCount: '{{count}} гаманець',
        walletsCount_plural: '{{count}} гаманців',
        balanceSummary: 'Підсумок балансів',
        noWallets: 'Гаманці не знайдено'
      },
      currency: {
        usd: 'Долар США',
        uah: 'Українська гривня',
        eur: 'Євро'
      },
      messages: {
        productAdded: 'Товар успішно додано',
        productUpdated: 'Товар успішно оновлено',
        productDeleted: 'Товар успішно видалено',
        stockUpdated: 'Запас успішно оновлено'
      },
      addExpense: 'Додати витрати',
      addSales: 'Додати продажі',
      profile: 'Профіль',
      settings: 'Налаштування',
      logout: 'Вийти',
      payments: {
        errors: {
          invalidCustomerId: 'Недійсний ID клієнта. Оновіть сторінку та спробуйте ще раз.',
          invalidDestination: 'Недійсне призначення платежу. Оберіть гаманець або рахунок каси ще раз.',
          invalidAmount: 'Недійсна сума платежу. Введіть правильну суму.',
          invalidDestinationId: 'Недійсний ID призначення. Оберіть гаманець або рахунок каси ще раз.',
          insufficientFunds: 'Недостатньо коштів для цього платежу.',
          paymentFailed: 'Платіж не вдався. Спробуйте ще раз.'
        },
        customerPaymentSuccess: 'Платіж клієнта успішно оброблено',
        customerPaymentError: 'Не вдалося обробити платіж клієнта',
        supplierPaymentSuccess: 'Платіж постачальнику успішно оброблено',
        supplierPaymentError: 'Не вдалося обробити платіж постачальнику',
        paymentTooLarge: 'Платіж занадто великий',
        paymentExceedsLimit: 'Платіж у розмірі {{payment}} перевищує ліміт {{max}} (2x поточний борг {{current}})',
        largePaymentWarning: 'Великий платіж',
        largePaymentInfo: 'Ви збираєтесь зробити платіж у розмірі {{payment}}, коли клієнт винен лише {{debt}}. Це створить кредитний баланс.',
        cashoutProcessed: 'Вивід коштів клієнта успішно оброблено',
        cashoutFailed: 'Не вдалося обробити вивід коштів',
        processCashout: 'Обробити вивід коштів',
        cashoutAmount: 'Сума виведення',
        balanceAfter: 'Баланс після',
        immediateProcessing: 'Негайна обробка',
        enterAmountToCashout: 'Введіть суму для виведення',
        available: 'Доступно',
        enterReasonForCashout: 'Введіть причину виведення (наприклад, "Клієнт попросив готівку")',
        cashoutConfirmation: 'Це негайно обробить виведення та зменшить кредит клієнта. Цю дію неможливо скасувати.',
        cashoutProcess: 'Процес виведення',
        cashoutDescription: 'Ця дія негайно обробить виведення та зменшить кредит клієнта. Джерело гаманця буде автоматично вибрано системою.',
        processCashoutForCredit: 'Обробити виведення коштів клієнта',
        processCashoutDescription: 'Обробити виведення для кредитного балансу клієнта - часткові суми дозволені',
        currentCreditBalance: 'Поточний кредитний баланс',
        cashoutSummary: 'Підсумок виведення'
      },
      transactions: {
        descriptions: {
          customerPayment: 'Клієнт {{customerName}} заплатив - {{description}}',
          paymentFromCustomer: 'Платіж від клієнта {{customerName}}',
          orderPayment: 'Оплата за замовлення #{{orderId}}',
          orderPaymentFrom: 'Замовлення #{{orderId}} оплата від {{customerName}}',
          supplierPayment: 'Платіж постачальнику {{supplierName}}',
          supplierOrderPayment: 'Постачальник {{supplierName}} платіж за замовлення #{{orderId}}',
          casaTransfer: 'Переказ до каси',
          casaTransferWithReason: 'Переказ до каси: {{reason}}',
          transferFromCasa: 'Переказ з каси до {{destination}}',
          walletTransfer: 'Переказ з {{fromWallet}} до {{toWallet}}',
          customerRefund: 'Повернення клієнту {{customerName}}',
          orderRefund: 'Повернення за замовлення #{{orderId}}',
          balanceAdjustment: 'Коригування балансу: {{reason}}',
          writeOff: 'Списання: {{reason}}',
          expense: 'Витрати: {{description}}',
          companyExpense: 'Витрати компанії на {{purpose}}',
          currencyConversion: 'Конвертація валюти з {{fromCurrency}} в {{toCurrency}}',
          ordersSettled: 'Погашено {{orderCount}} замовлень: {{orderNumbers}}',
          fundsAdded: 'Кошти додано: {{description}}',
          withdrawal: 'Зняття: {{reason}}'
        },
        types: {
          in: 'Надходження',
          out: 'Списання',
          payment: 'Платіж',
          refund: 'Повернення',
          adjustment: 'Коригування',
          transfer: 'Переказ'
        },
        status: {
          pending: 'В очікуванні',
          completed: 'Завершено',
          failed: 'Не вдалося',
          cancelled: 'Скасовано',
          processing: 'В обробці'
        },
        paymentMethods: {
          cash: 'Готівка',
          card: 'Картка',
          bank_transfer: 'Банківський переказ',
          wallet: 'Гаманець',
          casa: 'Каса'
        },
        references: {
          order: 'Замовлення #{{id}}',
          customer: 'Клієнт #{{id}}',
          supplier: 'Постачальник #{{id}}',
          invoice: 'Рахунок #{{id}}'
        }
      },
      time: {
        justNow: 'щойно',
        secondAgo: '1 секунду тому',
        secondsAgo: '{{count}} секунд тому',
        minuteAgo: '1 хвилину тому',
        minutesAgo: '{{count}} хвилин тому',
        hourAgo: '1 годину тому',
        hoursAgo: '{{count}} годин тому',
        yesterday: 'вчора',
        dayAgo: '1 день тому',
        daysAgo: '{{count}} днів тому',
        weekAgo: '1 тиждень тому',
        weeksAgo: '{{count}} тижнів тому',
        monthAgo: '1 місяць тому',
        monthsAgo: '{{count}} місяців тому',
        yearAgo: '1 рік тому',
        yearsAgo: '{{count}} років тому'
      }
    }
  },
  tr: {
    translation: tr
  }
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: false,
    interpolation: {
      escapeValue: false,
    },
  })

export default i18n
